import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, BookOpen } from 'lucide-react';

const latestPosts = [
  {
    title: 'Can I Get a Massage While Pregnant?',
    excerpt:
      'An honest trimester-by-trimester answer — what\'s safe, when to wait, and why your provider decides first.',
    date: 'February 14, 2026',
    slug: 'can-i-get-a-massage-while-pregnant',
    image:
      'https://images.unsplash.com/photo-1519824145371-296894a0daa9?w=1200&auto=format&fit=crop&q=80',
    category: 'Personal Notes',
    readTime: '7 min read',
  },
  {
    title: 'Should I Tip My Massage Therapist?',
    excerpt:
      '15–20% is customary — plus the quick math for every session length and when tipping isn\'t expected at all.',
    date: 'February 14, 2026',
    slug: 'should-i-tip-my-massage-therapist',
    image:
      'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&auto=format&fit=crop&q=80',
    category: 'Personal Notes',
    readTime: '6 min read',
  },
  {
    title: 'How Often Should I Get a Massage?',
    excerpt:
      'A therapist\'s honest cadence framework — by goal, by lifestyle, and by what your body\'s actually asking for.',
    date: 'February 13, 2026',
    slug: 'how-often-should-i-get-a-massage',
    image:
      'https://images.unsplash.com/photo-1519824145371-296894a0daa9?w=1200&auto=format&fit=crop&q=80',
    category: 'Personal Notes',
    readTime: '7 min read',
  },
];

const LatestBlogSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      id="latest-blog"
      className="py-20 bg-white"
      data-testid="latest-blog-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          className={`flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-6 transition-all duration-700 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
          }`}
        >
          <div>
            <div className="flex items-center gap-2 mb-3 text-teal-700">
              <BookOpen className="w-5 h-5" />
              <span className="text-xs font-semibold uppercase tracking-wider">
                From the Blog
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-serif text-gray-800 leading-tight">
              Wellness insights from mid-town Sacramento.
            </h2>
            <p className="mt-4 text-gray-600 max-w-2xl">
              Practical guides on massage therapy, recovery, and wellness — written by certified therapist Ayumi Suzuki.
            </p>
          </div>

          <Link
            to="/blog"
            className="hidden md:inline-flex items-center text-teal-700 hover:text-teal-800 font-medium group"
            data-testid="view-all-blog-desktop"
          >
            View all articles
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>

        {/* Posts grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {latestPosts.map((post, idx) => (
            <article
              key={post.slug}
              className={`group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
              }`}
              style={{ transitionDelay: isVisible ? `${idx * 120}ms` : '0ms' }}
              data-testid={`latest-blog-card-${idx}`}
            >
              <Link to={`/blog/${post.slug}`} className="block">
                <div className="aspect-video overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
              </Link>

              <div className="p-6">
                <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                  <span className="bg-teal-100 text-teal-700 px-2.5 py-1 rounded-full font-medium">
                    {post.category}
                  </span>
                  <span className="flex items-center">
                    <Calendar className="w-3.5 h-3.5 mr-1" />
                    {post.date}
                  </span>
                  <span>{post.readTime}</span>
                </div>

                <Link to={`/blog/${post.slug}`}>
                  <h3 className="text-lg font-serif text-gray-800 mb-3 group-hover:text-teal-700 transition-colors leading-snug">
                    {post.title}
                  </h3>
                </Link>

                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                  {post.excerpt}
                </p>

                <Link
                  to={`/blog/${post.slug}`}
                  className="inline-flex items-center text-teal-700 hover:text-teal-800 text-sm font-medium group/link"
                  data-testid={`latest-blog-read-${idx}`}
                >
                  Read article
                  <ArrowRight className="w-4 h-4 ml-1 group-hover/link:translate-x-1 transition-transform duration-300" />
                </Link>
              </div>
            </article>
          ))}
        </div>

        {/* Mobile "View all" */}
        <div className="mt-10 text-center md:hidden">
          <Link
            to="/blog"
            className="inline-flex items-center text-teal-700 hover:text-teal-800 font-medium"
            data-testid="view-all-blog-mobile"
          >
            View all articles
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default LatestBlogSection;
